import Box from '@mui/material/Box';
import { dynamicFormField, EditDrawerProps, fieldType } from 'src/types/apps/masterdataTypes';
import FormInput from './FormInput';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { DtoPropertyDto, MasterDataColumnDto } from 'src/models/systemconfig';
import { addMasterDataRow, getMasterDataRow, updateMasterDataRow } from '../../services/masterdata';
import * as yup from 'yup';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSettings } from 'src/@core/hooks/useSettings';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import VigoSpinner from 'src/@vigo/vigo-spinner';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { VigoButton } from '../vigo-button';
import { useVigoCamelCase } from 'src/@core/hooks/useVigoCamelCase';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';

const VigoMasterDataEditDrawer = ({
  open,
  rowId,
  endPoint,
  serviceLocation,
  masterDataObject,
  refresh,
  joinColumn,
  parentId,
  toggle,
  title,
  hiddenFields = []
}: EditDrawerProps) => {
  // State
  const [formFields, setFormFields] = useState<dynamicFormField[]>([]);
  const [displayFields, setDisplayFields] = useState<dynamicFormField[]>([]);
  const [recordDeleted, setRecordDeleted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(false);

  // Refs
  const formRef = useRef(null);

  // the system columns we dont render for edit
  const systemColumnList = useMemo(() => {
    return [
      'CreatedOn',
      'CreatedBy',
      'UpdatedOn',
      'UpdatedBy',
      'IsDeleted',
      'DeletedBy',
      'DeletedOn',
      'OwnerTenantId',
      'OwnerSiteId',
      'OwnerPartnerId',
      'AttachmentCount',
      ...hiddenFields
    ];
  }, [hiddenFields]);

  // Hooks
  const prod = useGetProductLevel();
  const { settings } = useSettings();
  const { t } = useVigoTranslation();
  const { vigoToast } = useVigoToast({});
  const { toCamelCase } = useVigoCamelCase();
  const mutationsRunning = useIsMutating();

  // generate the validation scheme
  const validationSchema = (fields: any[]) => {
    const schema = fields.reduce((schema: any, field: { validations: any; field: any }) => {
      return field.validations ? { ...schema, [field.field]: field.validations } : schema;
    }, {});

    return yup.object().shape(schema);
  };
  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(validationSchema(displayFields))
  });
  const { handleSubmit, reset, control, setValue, getValues } = methods;

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (values: any) => {
      if (values['id']) {
        return updateMasterDataRow({
          data: values,
          serviceLocation: serviceLocation,
          endPoint: endPoint
        });
      } else {
        setValue(toCamelCase(joinColumn), parentId);
        values[toCamelCase(joinColumn)] = parentId;

        return addMasterDataRow({
          data: values,
          serviceLocation: serviceLocation,
          endPoint: endPoint
        });
      }
    }
  });

  // if the column has any depenancies,  clear them once the cooumn has changed
  const clearDependancies = useCallback(
    (column?: string) => {
      if (column) {
        // clear contents of form
        setValue(column, '');

        // now clear from value
        if (formRef && formRef.current) {
          const newcol = toCamelCase(column);
          const control: any = formRef.current[newcol];
          control.value = '';
        }
      }
    },
    [setValue, toCamelCase]
  );

  // if the column has a filter resolve it
  const evaluateFilter = useCallback(
    (filter: string) => {
      if (filter === '') return '';

      if (!filter.includes('[')) {
        return filter;
      }

      // just get the filter from another field on the screen
      const fieldname = filter.split('[')[1].split(']')[0];

      if (fieldname) {
        filter = filter.split('[')[0];

        const value = getValues(fieldname);

        if (!value || value == '') {
          return '';
        }

        filter = filter + "'" + value + "'";

        return filter;
      }

      return '';
    },
    [getValues]
  );

  // get the value out of the row for the column
  function getFieldValue(source: any, column: any): string {
    if (!source) {
      return '';
    }

    let returnval = source[column];
    if (returnval === null || returnval === undefined) {
      returnval = '';
    }

    return returnval;
  }

  // convert db field to field type
  function getFieldType(value: string | undefined): fieldType {
    switch (value) {
      case 'int':
        return 'number';
      case 'string':
        return 'string';
      case 'bool':
        return 'boolean';
      case 'uniqueidentifier':
        return 'lookup';
      case 'datetimeoffset':
        return 'date';
      default:
        return 'string';
    }
  }

  // build the logic for validation
  function buildValidation(column: MasterDataColumnDto): any {
    let fieldValidation: any;

    if (column.minLength != null && column.minLength > 0) {
      fieldValidation = yup.string().required(column.name + ' is required');
    }

    return fieldValidation;
  }

  const buildEditorForm = useCallback(
    (rowresponse?: any) => {
      const columns: MasterDataColumnDto[] = masterDataObject?.masterDataColumns ?? [];
      const localFields: dynamicFormField[] = [];
      const localDisplayFields: dynamicFormField[] = [];
      const localValues: any = {};

      columns.forEach((column: MasterDataColumnDto) => {
        let readonly = false;
        const dtoProperty: DtoPropertyDto | undefined = masterDataObject?.dtoProperties?.find(
          x => x.id == column.dtoPropertyId
        );

        const dependancyProperty: string | undefined = masterDataObject?.masterDataColumns?.find(
          x => x.id == column.lookupDependencyId
        )?.name;

        const lookupPropertyDisplayName = toCamelCase(column.lookupDisplayProperty);

        const filledinCodeField =
          column.name != null &&
          column.name == 'Code' &&
          getFieldValue(rowresponse?.data, toCamelCase(column.name)) != ''
            ? true
            : false;

        if (filledinCodeField === true && prod !== Products.Quantum) {
          readonly = true;
        }

        if (!column.isEditable && prod !== Products.Quantum) {
          readonly = true;
        }

        if (rowresponse?.data?.isDeleted) {
          readonly = true;
        }

        const lookupEndpoint =
          masterDataObject?.lookupData != undefined
            ? masterDataObject.lookupData?.find(x => x.lookupDtoId == column.lookupDtoId)?.lookupEndpoint
            : '';

        if (column.name != undefined && systemColumnList.indexOf(column.name) === -1 && !column.isDeleted) {
          const formField: dynamicFormField = {
            type: getFieldType(masterDataObject?.propertyTypes?.find(x => x.id == dtoProperty?.propertyTypeId)?.code),
            field: toCamelCase(column.name),
            label: column.name != null ? column.name.charAt(0).toUpperCase() + column.name.slice(1) : '',
            value: getFieldValue(rowresponse?.data, toCamelCase(column.name)),
            maxlen: column.maxLength != null && column.maxLength > 0 ? column.maxLength : 1000,
            minlen: column.minLength,
            required: column.minLength != null && column.minLength > 0 ? true : false,
            validationType: 'string',
            filter: column.lookupDtoFilter ? column.lookupDtoFilter : '',
            evaluateFilter: evaluateFilter,
            lookupServiceLocation: lookupEndpoint != null ? lookupEndpoint.split('.')[0] : '',
            lookupServiceEndPoint: lookupEndpoint != null ? lookupEndpoint.split('.')[1] : '',
            lookupServiceDisplayName: lookupPropertyDisplayName,
            readonly: readonly || readOnly,
            isVisible: column.isVisible ? column.isVisible : false,
            validations: buildValidation(column),
            dependancyField: dependancyProperty,
            clearDependancies: clearDependancies
          };
          if (column.name != undefined) {
            localValues[column.name] = getFieldValue(rowresponse?.data, toCamelCase(column.name));
          }
          localFields.push(formField);
          if (systemColumnList.indexOf(formField.field) === -1) {
            localDisplayFields.push(formField);
          }
        }
      });

      return { localFields, localDisplayFields };
    },
    [
      clearDependancies,
      evaluateFilter,
      masterDataObject?.dtoProperties,
      masterDataObject?.lookupData,
      masterDataObject?.masterDataColumns,
      masterDataObject?.propertyTypes,
      prod,
      readOnly,
      systemColumnList,
      toCamelCase
    ]
  );

  const onSubmit = (values: any) => {
    return vigoToast.promise(
      values['id'] ? 'Updating row' : 'Adding row',
      values['id'] ? 'Row updated' : 'Row added',
      'Error Saving Data ',
      true,
      saveMutation.mutateAsync(values, {
        onSuccess: data => {
          refresh?.(data.data.data);
          handleClose();
        },
        onError: data => {
          //@ts-ignore
          if (data.data.data.status === '409') {
            //@ts-ignore
            vigoToast.error(data.data.data.message);
          } else {
            refresh?.({});
            //@ts-ignore
            vigoToast.error(data.data.data.message);
          }
        }
      })
    );
  };

  const handleClose = useCallback(
    (_event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        // Prevent closing when clicking outside the dialog
        return;
      }

      setFormFields([]);
      setReadOnly(false);
      reset();
      toggle?.({});
    },
    [reset, toggle]
  );

  useEffect(() => {
    if (open) {
      setLoading(true);

      if (rowId) {
        // Build form records with data from fetched row
        getMasterDataRow({
          endPoint: endPoint,
          serviceLocation: serviceLocation,
          showDeleted: settings.appShowDeleted,
          id: rowId
        }).then(rowresponse => {
          const res = buildEditorForm(rowresponse);

          setRecordDeleted(rowresponse.data.isDeleted);
          setFormFields(res.localFields);
          setDisplayFields(res.localDisplayFields);
          setLoading(false);

          if (
            prod !== Products.Quantum &&
            Object.hasOwn(rowresponse.data, 'ownerTenantId') &&
            rowresponse.data.ownerTenantId === '00000000-0000-0000-0000-000000000000'
          ) {
            setReadOnly(true);
          }
        });
      } else {
        // Build form records from MasterDataObject - (New Record from AutoComplete)
        const res = buildEditorForm();

        setFormFields(res.localFields);
        setDisplayFields(res.localDisplayFields);
        setLoading(false);
      }
    }
  }, [buildEditorForm, endPoint, open, prod, rowId, serviceLocation, settings.appShowDeleted]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      keepMounted={false}
      onClose={handleClose}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description">
      {loading && <VigoSpinner customSx={{ height: '15vh' }} name="masterDataEditorSpinner" />}

      {loading == false && (
        <>
          <DialogTitle id="scroll-dialog-title">
            <div>
              <VigoTypography value={title} variant="h6" />
              {readOnly && (
                <Box bgcolor="#FF8C90" padding={3} mt={5}>
                  <VigoTypography
                    value="You do not have permission to edit this record"
                    sx={{ mt: 1, textAlign: 'center' }}
                  />
                </Box>
              )}
            </div>
          </DialogTitle>
          <DialogContent>
            <div>
              <Box sx={{ p: 5 }}>
                <form onSubmit={handleSubmit(onSubmit)} id="mdform" ref={formRef}>
                  {formFields.map(item => (
                    <FormInput key={item.field} setValue={setValue} reset={reset} fieldData={item} control={control} />
                  ))}
                </form>
              </Box>
            </div>
          </DialogContent>
          <DialogActions>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!recordDeleted && (
                <VigoButton
                  name="submit"
                  value="Submit"
                  variant="contained"
                  size="large"
                  type="submit"
                  form="mdform"
                  disabled={Boolean(mutationsRunning) || readOnly}
                  sx={{ mr: 3 }}
                />
              )}
              <VigoButton
                name="cancel"
                value="Cancel"
                variant="outlined"
                size="large"
                color="secondary"
                onClick={handleClose}
              />
            </Box>
            {recordDeleted && (
              <Alert sx={{ mt: 5 }} severity="error">
                {t('Record is deleted')}
              </Alert>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default VigoMasterDataEditDrawer;
